/*
 * OBX Airwaves — NOAA Oregon Inlet Marine Conditions
 * Source: https://obxairwaves.com/weatherdata/marine.json
 * WeatherStar 4000+ v7.1.6-compatible module, loaded WITH the official bundle.
 * By default this display is DISABLED until you opt in to testing.
 */
import STATUS from '../../modules/status.mjs';
import WeatherDisplay from '../../modules/weatherdisplay.mjs';
import { registerDisplay } from '../../modules/navigation.mjs';

const DATA_URL = 'https://obxairwaves.com/weatherdata/marine.json';
const MAX_OBSERVATION_AGE_MS = 3 * 60 * 60 * 1000;
const FETCH_TIMEOUT_MS = 10000;

const validNumber = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : null);
const reading = (value, precision = 1) => {
    const numeric = validNumber(value);
    if (numeric === null) return 'N/A';
    return numeric.toFixed(precision);
};
const compass = (degrees) => {
    const numeric = validNumber(degrees);
    if (numeric === null || numeric < 0 || numeric > 360) return 'N/A';
    const points = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    return `${Math.round(numeric)}° ${points[Math.round(numeric / 45) % 8]}`;
};
const observationTime = (dateString) => {
    const ms = Date.parse(dateString);
    if (!Number.isFinite(ms)) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
        timeZoneName: 'short',
    }).format(new Date(ms)).toUpperCase();
};

class ObxMarine extends WeatherDisplay {
    constructor() {
        // ID 12 is unused by the standard v7.1.6 displays. Fail-closed check in installer.
        // Disabled by default: opt in via checkbox or ?obx-marine=true.
        super(12, 'obx-marine', 'OBX Marine Conditions', false);
        this.refreshTime = 15 * 60 * 1000;
        this.timing.totalScreens = 1;
        this.timing.baseDelay = 9000;
        this.timing.delay = 1;
    }

    async getData(weatherParameters, refresh = false) {
        if (!super.getData(weatherParameters, refresh)) return;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
        try {
            const url = `${DATA_URL}?nocache=${Date.now()}`;
            const response = await fetch(url, { mode: 'cors', cache: 'no-store', signal: controller.signal });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const next = await response.json();
            if (next.station_id !== '44095') throw new Error('Unexpected station');
            const observedMs = Date.parse(next.observed_at_utc);
            const observationAge = Date.now() - observedMs;
            if (!Number.isFinite(observedMs) || observationAge < -10 * 60000 || observationAge > MAX_OBSERVATION_AGE_MS) {
                throw new Error('NOAA observation unavailable or stale');
            }
            if ([next.wave_height_ft, next.dominant_period_sec, next.average_period_sec,
                next.mean_wave_direction_deg, next.water_temperature_f].every((value) => validNumber(value) === null)) {
                throw new Error('All marine measurements unavailable');
            }
            this.data = next;
            this.timing.totalScreens = 1;
            this.calcNavTiming();
            this.getDataCallback();
            this.setStatus(STATUS.loaded);
        } catch (error) {
            // Fail closed. An unavailable NOAA feed must not stall or show stale readings.
            this.data = undefined;
            this.timing.totalScreens = 0;
            this.calcNavTiming();
            this.getDataCallback();
            this.setStatus(STATUS.noData);
            console.warn('OBX Marine slide skipped:', error?.message ?? error);
        } finally {
            clearTimeout(timeout);
        }
    }

    drawCanvas() {
        super.drawCanvas();
        if (!this.elem || !this.data) { this.finishDraw(); return; }
        const fields = {
            '.obx-marine-height': `${reading(this.data.wave_height_ft)} FT`,
            '.obx-marine-dpd': `${reading(this.data.dominant_period_sec, 0)} SEC`,
            '.obx-marine-apd': `${reading(this.data.average_period_sec)} SEC`,
            '.obx-marine-direction': compass(this.data.mean_wave_direction_deg),
            '.obx-marine-temp': `${reading(this.data.water_temperature_f)}°F`,
            '.obx-marine-observed': observationTime(this.data.observed_at_utc),
        };
        for (const [selector, value] of Object.entries(fields)) {
            const element = this.elem.querySelector(selector);
            if (element) element.textContent = value;
        }
        this.finishDraw();
    }
}

registerDisplay(new ObxMarine());
